import React from 'react';
import { Link } from 'react-router-dom';
import { VISO_PLATFORM_LIST } from '@/data/vplatforms';
import { Button } from '@/components/ui/button';
import { ArrowRight, Box, Warehouse, Factory, Database } from 'lucide-react';

const iconMap = {
  'viso-yard': Box,
  'viso-warehouse': Warehouse,
  'viso-factory': Factory,
  'viso-data': Database,
};

export default function PlatformPage() {
  return (
    <div>
      <section className="relative py-24 border-b border-white/5">
        <div className="absolute inset-0 viso-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-xs font-mono uppercase tracking-widest text-cyan-300 mb-3">
            The Visotonics Platform
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-bold max-w-4xl leading-[1.05]">
            <span className="text-gradient">Four environments, one vision platform.</span>
          </h1>
          <p className="text-slate-400 mt-5 max-w-2xl">
            One Physical AI stack — deployed on the CCTV you already own. From gate to line to dock, every checkpoint on the record.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-5">
          {VISO_PLATFORM_LIST.map((p, i) => {
            const Icon = iconMap[p.slug] || Box;
            return (
              <Link
                key={p.slug}
                to={`/platform/${p.slug}`}
                className="card-glow group relative rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-transparent overflow-hidden hover:border-cyan-400/30 transition-all"
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img
                    src={p.poster}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#07090c] via-[#07090c]/60 to-transparent" />
                  <div className="absolute inset-0 viso-grid opacity-30" />
                  <div className="absolute bottom-4 left-5 right-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-cyan-400/15 border border-cyan-400/30 flex items-center justify-center backdrop-blur">
                        <Icon className="w-5 h-5 text-cyan-300" />
                      </div>
                      <div>
                        <div className={`font-display text-2xl font-bold bg-gradient-to-r ${p.accent} bg-clip-text text-transparent`}>
                          {p.name}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">
                          {p.tagline}
                        </div>
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-cyan-300 group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-slate-400 text-sm leading-relaxed">{p.hero}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {p.products.slice(0, 5).map((prod) => (
                      <span
                        key={prod.id}
                        className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded bg-white/5 border border-white/10 text-slate-300"
                      >
                        {prod.name}
                      </span>
                    ))}
                    {p.products.length > 5 && (
                      <span className="text-[10px] font-mono px-2 py-1 rounded text-slate-500">
                        +{p.products.length - 5} more
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="border-t border-white/5 py-16">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-display text-2xl md:text-3xl font-bold">
              Bring a camera feed. We&apos;ll read it live.
            </div>
            <p className="text-slate-400 mt-2 max-w-xl">
              From gate to line to dock — the platform runs on your existing CCTV.
            </p>
          </div>
          <Link to="/contact">
            <Button className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-medium">
              Talk to us <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
